<template>
  <f7-toolbar
    position="top"
    class="parts-toolbar"
  >
    <div>
      <f7-button
        :color="currentTab === Tabs.PENDING ? 'steel-blue' : 'gray'"
        fill
        title="Mostrar los partes pendientes"
        @click="changeTab(Tabs.PENDING)"
      >
        Pendientes
      </f7-button>

      <f7-button
        :color="currentTab === Tabs.FINISHED ? 'steel-blue' : 'gray'"
        fill
        title="Mostrar los partes finalizados"
        @click="changeTab(Tabs.FINISHED)"
      >
        Finalizados
      </f7-button>
    </div>

    <div class="parts-toolbar__actions">
      <UCDatePicker
        range-picker
        class="parts-toolbar__actions__date"
        :disabled="isLoadingWorkParts"
        @onchange="(value) => setSelectedDate(value, false)"
      />

      <f7-button
        :preloader="isRefreshingWorkParts"
        :loading="isRefreshingWorkParts"
        type="button"
        title="Refrescar los partes de trabajo"
        :disabled="isLoadingWorkParts"
        @click="setSelectedDate(selectedDate, true)"
      >
        <UCSvgIcon
          name="ic_refresh"
          width="38px"
          height="38px"
        />
      </f7-button>
    </div>
  </f7-toolbar>
</template>

<script setup>
// @ts-check
import { onMounted, ref, watch } from 'vue';
import { useRepairParts } from '@/composables/useRepairParts';
import { Tabs } from '@/js/enums/externalRepairParts';

import UCDatePicker from '@/components/common/UCDatePicker.vue';
import UCSvgIcon from '@/components/common/UCSvgIcon.vue';

const emit = defineEmits(['tabChanged']);

const { worker } = useRepairParts();

function useTabs() {
  const { currentTab } = useRepairParts();

  /**
   * @param {number} newTab - The tab to change.
   * @returns {void}
   */
  function changeTab(newTab) {
    if (currentTab.value === newTab) {
    // Same tab, don't need to change
      return;
    }

    if (currentTab.value === Tabs.FINISHED) {
      currentTab.value = Tabs.PENDING;
    } else {
      currentTab.value = Tabs.FINISHED;
    }

    emit('tabChanged', newTab);
  }

  return {
    currentTab,
    changeTab,
  };
}

/**
 * @param {object} options
 * @param {(forced: boolean) => Promise<void>} options.processWorkPartsLoading
 */
function useDateSelection({ processWorkPartsLoading }) {
  const { selectedDate } = useRepairParts();

  /**
   * @param {typeof selectedDate['value']} date - The date to set.
   * @param {boolean} forced - Whether to force the update.
   * @returns {void}
   */
  function setSelectedDate(date, forced) {
    if (!date) {
      return;
    }

    if (forced || selectedDate.value !== date) {
      selectedDate.value = date;
      processWorkPartsLoading(forced);
    }
  }

  function setInitialDate() {
    if (!worker.value) {
      return;
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    setSelectedDate({
      Init: currentDate,
      End: currentDate,
    }, false);
  }

  watch(worker, () => {
    setInitialDate();
  }, { once: true });

  onMounted(() => {
    setInitialDate();
  });

  return {
    selectedDate,
    setSelectedDate,
  };
}

function useWorkPartsLoading() {
  const isRefreshingWorkParts = ref(false);

  const {
    isLoadingWorkParts,
    loadWorkParts,
  } = useRepairParts();

  /**
   *
   * @param {boolean} forced - Whether to force the update.
   * @returns {Promise<void>}
   */
  async function processWorkPartsLoading(forced) {
    if (!worker.value) {
      return;
    }

    if (forced) {
      isRefreshingWorkParts.value = true;
    }

    try {
      await loadWorkParts();
    } finally {
      isRefreshingWorkParts.value = false;
    }
  }

  return {
    isRefreshingWorkParts,
    isLoadingWorkParts,
    processWorkPartsLoading,
  };
}

const { currentTab, changeTab } = useTabs();

const {
  isRefreshingWorkParts,
  isLoadingWorkParts,
  processWorkPartsLoading,
} = useWorkPartsLoading();

const {
  selectedDate,
  setSelectedDate,
} = useDateSelection({ processWorkPartsLoading });
</script>

<style scoped lang="scss">
.parts-toolbar {
  --f7-toolbar-height: 76px;

  // TODO: Remove when migrated to new styles
  --f7-button-padding-horizontal: 10px;
  --f7-button-height: 44px;
  --f7-button-small-height: 40px;
  --f7-bars-shadow-bottom-image: linear-gradient(180deg, rgb(0 0 0 / 25%) 0%, rgb(0 0 0 / 8%) 40%, rgb(0 0 0 / 4%) 50%, transparent 90%, transparent);
  --f7-toolbar-top-shadow-image: var(--f7-bars-shadow-bottom-image);
  //

  background-color: var(--his-tierra-white);

  .toolbar-inner {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
      padding-left: var(--f7-typography-padding);
      padding-right: var(--f7-typography-padding);
    }
  }

  &__actions {
    &__date {
      flex: 0 0 80%;
    }
  }
}
</style>
